import type { Logger, MessageContext } from "roarr"
import { logLevels, Roarr, ROARR } from "roarr"
import { createLogWriter } from "@roarr/browser-log-writer"
import type { Primitive } from "type-fest"
import type { ErrorObject } from "serialize-error"
import { captureException, captureMessage } from "@sentry/vue"

/* eslint-disable @typescript-eslint/no-explicit-any */

const browserLogWriter = createLogWriter()

// The Roarr browser writer only emits to the console when the dev-only ROARR_LOG
// flag is set (see below), so in production every logger.* call would otherwise be
// discarded. Bridge warn/error logs to Sentry/GlitchTip so handled errors keep a
// durable diagnostic trail in production. Console output is unchanged. Call sites
// that carry an error should pass a serialized error in `context.error` (raw Error
// objects do not survive Roarr's JSON serialization) so it reaches captureException.
ROARR.write = (message) => {
  browserLogWriter(message)
  try {
    const { message: text, context } = JSON.parse(message) as {
      message: string
      context?: LoggerContext & { logLevel?: number }
    }
    const logLevel = context?.logLevel ?? 0
    if (logLevel < logLevels.warn) return
    if (context?.error) {
      captureException(context.error, { extra: { logMessage: text, namespace: context.namespace } })
    } else {
      captureMessage(text, logLevel >= logLevels.error ? "error" : "warning")
    }
  } catch {
    // Non-JSON writer payloads are ignored.
  }
}

export interface LoggerContext {
  namespace?: string
  error?: Primitive | ErrorObject | unknown

  [key: string]: any
}

const isProduction = import.meta.env.PROD
if (!isProduction) {
  if (!localStorage.getItem("ROARR_LOG")) {
    localStorage.setItem("ROARR_LOG", "true")
  }
  if (!localStorage.getItem("ROARR_FILTER")) {
    localStorage.setItem('ROARR_FILTER', 'context.logLevel:>=0')
  }
}

export function createLogger(namespaceOrContext?: string | MessageContext): Logger<LoggerContext> {
  if ("string" === typeof namespaceOrContext) {
    return Roarr.child({
      namespace: namespaceOrContext
    }) as Logger<LoggerContext>
  }

  if ("object" === typeof namespaceOrContext) {
    return Roarr.child(namespaceOrContext) as Logger<LoggerContext>
  }

  return Roarr as Logger<LoggerContext>
}

